import PropTypes from 'prop-types';
import { globalizeSelectors } from '../Utils';
import type {
  PrintCopies,
  PrintLayout,
  PrintPreviewAction,
  PrintPreviewOrder,
  PrintPreviewOrderPayload,
  PrintPreviewState,
  PrintPrinter,
} from '../types/PrintPreview';
/**
 * Just a simple flag to notify the UI that progress modal needs to be shown!
 */

/*
 * action types
 */

const types = {
  SET_VISIBLE: 'PRINT_PREVIEW/SET_VISIBLE',
  SET_ORDER: 'PRINT_PREVIEW/SET_ORDER',
  SET_LAYOUT: 'PRINT_PREVIEW/SET_LAYOUT',
  SET_PRINTER: 'PRINT_PREVIEW/SET_PRINTER',
  SET_NUMBER_OF_COPIES: 'PRINT_PREVIEW/SET_NUMBER_OF_COPIES',
} as const;

function payloadAs<Payload>(action: PrintPreviewAction): Payload {
  return action.payload as Payload;
}

/*
 * action creators
 */

const actions = {
  setVisible: (visible: boolean): PrintPreviewAction<boolean> => ({
    type: types.SET_VISIBLE,
    payload: visible,
  }),
  setOrder: (
    order: PrintPreviewOrder,
    canNumber = 1
  ): PrintPreviewAction<PrintPreviewOrderPayload> => ({
    type: types.SET_ORDER,
    payload: { order, canNumber },
  }),
  setLayout: (layout: PrintLayout): PrintPreviewAction<PrintLayout> => ({
    type: types.SET_LAYOUT,
    payload: layout,
  }),
  setPrinter: (printer: PrintPrinter): PrintPreviewAction<PrintPrinter> => ({
    type: types.SET_PRINTER,
    payload: printer,
  }),
  setCopies: (copies: PrintCopies): PrintPreviewAction<PrintCopies> => ({
    type: types.SET_NUMBER_OF_COPIES,
    payload: copies,
  }),
};

const mountPath = 'print_preview'; // mount point in global state, must match root reducer

const propType = PropTypes.shape({
  visible: PropTypes.bool,
  order: PropTypes.shape({}),
  canNumber: PropTypes.number,
  layout: PropTypes.shape({}),
  copies: PropTypes.any,
  printer: PropTypes.any,
});

const initialState: PrintPreviewState = {
  visible: false,
  order: {},
  canNumber: 1,
  layout: null,
  printer: null,
  copies: 1,
};

function reducer(
  state: PrintPreviewState = initialState,
  action: PrintPreviewAction
): PrintPreviewState {
  switch (action.type) {
    case types.SET_VISIBLE: {
      return { ...state, visible: payloadAs<boolean>(action) };
    }
    case types.SET_ORDER: {
      const { order, canNumber } = payloadAs<PrintPreviewOrderPayload>(action);
      return { ...state, order, canNumber };
    }
    case types.SET_LAYOUT: {
      return { ...state, layout: payloadAs<PrintLayout>(action) };
    }
    case types.SET_PRINTER: {
      return { ...state, printer: payloadAs<PrintPrinter>(action) };
    }
    case types.SET_NUMBER_OF_COPIES: {
      return { ...state, copies: payloadAs<PrintCopies>(action) };
    }
    default: {
      return state;
    }
  }
}

// selectors

const localSelectors = {
  visible: (state: PrintPreviewState): boolean => state.visible,
  order: (state: PrintPreviewState): PrintPreviewOrder => state.order,
  layout: (state: PrintPreviewState): PrintLayout => state.layout,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export {
  types as actionTypes,
  actions as default,
  selectors,
  reducer,
  propType,
};
